import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  X, 
  Euro, 
  Plus, 
  Trash2, 
  Edit2, 
  Check,
  Lock,
  ReceiptEuro,
  CreditCard,
  User,
  Briefcase,
  ChevronDown,
  Settings,
  Coins,
  Wallet,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import DatePicker, { registerLocale } from 'react-datepicker';
import DatePickerWithInput from './DatePickerWithInput';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';
import { formatCaseName } from '../utils/caseNameFormatter';
import SearchableClientSelect from './SearchableClientSelect';

registerLocale('el', el);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to remove Greek accents
const removeAccents = (str) => {
  if (!str) return '';
  const accentMap = {
    'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
    'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
    'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
  };
  return str.toUpperCase().split('').map(char => accentMap[char] || char).join('');
};

// Format currency: 1234.56 -> "1.235 €" (no decimals — rounded)
const formatCurrency = (amount) => {
  const num = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  return Math.round(num).toLocaleString('de-DE') + ' €';
};

const ENTRY_TYPES = [
  { value: 'fee', label: 'Αμοιβή', icon: ReceiptEuro, color: 'text-red-700' },
  { value: 'expense', label: 'Έξοδο', icon: Euro, color: 'text-amber-700' },
  { value: 'payment', label: 'Πληρωμή', icon: CreditCard, color: 'text-slate-700' },
];

const FEE_TYPES = [
  { value: 'flat', label: 'Αμοιβή', icon: ReceiptEuro },
  { value: 'hourly', label: 'Χρονοχρέωση', icon: Clock },
];

const FinancialManagementModal = ({ isOpen, onClose, clients = [], cases = [], entries: caseEntries = [] }) => {
  // PIN State
  const [pinVerified, setPinVerified] = useState(false);
  const [hasPin, setHasPin] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [checkingPin, setCheckingPin] = useState(true);
  const [settingPin, setSettingPin] = useState(false);
  const [newPinInput, setNewPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  
  // Financial Data State
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    entry_type: 'fee',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    client_id: '',
    case_id: '',
    fee_type: 'flat',
    hours: '',
    hourly_rate: '',
    notes: '',
    is_paid: false,
    add_to_case_entries: true
  });
  
  // Filters
  const [filterClient, setFilterClient] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showDebts, setShowDebts] = useState(false);

  // Alphabetically sorted clients (Greek locale, accent-insensitive)
  const sortedClients = useMemo(() => {
    const getLabel = (c) => c.client_type === 'natural'
      ? removeAccents(`${c.surname || ''} ${c.name || ''}`.trim())
      : removeAccents(c.company_name || '');
    return [...clients].sort((a, b) =>
      getLabel(a).localeCompare(getLabel(b), 'el', { sensitivity: 'base' })
    );
  }, [clients]);

  // Check if PIN exists on mount
  useEffect(() => {
    if (isOpen) {
      // Check sessionStorage cache: if PIN was verified in the last 20 minutes, skip prompt
      try {
        const cachedAt = parseInt(sessionStorage.getItem('cp_financial_pin_verified_at') || '0', 10);
        if (cachedAt && Date.now() - cachedAt < 20 * 60 * 1000) {
          setPinVerified(true);
          setHasPin(true);
          setCheckingPin(false);
          fetchData();
          setFilterClient('');
          return;
        }
      } catch (e) { /* ignore sessionStorage errors */ }
      checkPinStatus();
      // Reset client filter when opening
      setFilterClient('');
    } else {
      // Reset state when modal closes (but keep sessionStorage cache)
      setPinVerified(false);
      setPinInput('');
      setShowForm(false);
      setEditingEntry(null);
      setShowSettings(false);
    }
  }, [isOpen]);

  const checkPinStatus = async () => {
    setCheckingPin(true);
    try {
      const response = await axios.get(`${API}/financial/has-pin`);
      setHasPin(response.data.has_pin);
      if (!response.data.has_pin) {
        // No PIN set - show setup
        setSettingPin(true);
      }
    } catch (error) {
      console.error('Error checking PIN:', error);
      toast.error('Σφάλμα ελέγχου PIN');
    } finally {
      setCheckingPin(false);
    }
  };

  const verifyPin = async () => {
    if (pinInput.length !== 4) {
      toast.error('Το PIN πρέπει να είναι 4 ψηφία');
      return;
    }
    try {
      await axios.post(`${API}/financial/verify-pin`, { pin: pinInput });
      setPinVerified(true);
      // Cache verification for 20 minutes
      try { sessionStorage.setItem('cp_financial_pin_verified_at', Date.now().toString()); } catch (e) { /* ignore */ }
      fetchData();
    } catch (error) {
      toast.error('Λάθος PIN');
      setPinInput('');
    }
  };

  const setPin = async () => {
    if (newPinInput.length !== 4 || !newPinInput.match(/^\d{4}$/)) {
      toast.error('Το PIN πρέπει να είναι 4 ψηφία');
      return;
    }
    if (newPinInput !== confirmPinInput) {
      toast.error('Τα PIN δεν ταιριάζουν');
      return;
    }
    try {
      await axios.post(`${API}/financial/set-pin`, { new_pin: newPinInput });
      toast.success('Το PIN ορίστηκε επιτυχώς');
      setHasPin(true);
      setSettingPin(false);
      setPinVerified(true);
      try { sessionStorage.setItem('cp_financial_pin_verified_at', Date.now().toString()); } catch (e) { /* ignore */ }
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα ορισμού PIN');
    }
  };

  const changePin = async () => {
    if (newPinInput.length !== 4 || !newPinInput.match(/^\d{4}$/)) {
      toast.error('Το νέο PIN πρέπει να είναι 4 ψηφία');
      return;
    }
    if (newPinInput !== confirmPinInput) {
      toast.error('Τα PIN δεν ταιριάζουν');
      return;
    }
    try {
      await axios.post(`${API}/financial/set-pin`, { 
        new_pin: newPinInput, 
        current_pin: pinInput 
      });
      toast.success('Το PIN άλλαξε επιτυχώς');
      setShowSettings(false);
      setNewPinInput('');
      setConfirmPinInput('');
      // Refresh cache timestamp since user is still authenticated
      try { sessionStorage.setItem('cp_financial_pin_verified_at', Date.now().toString()); } catch (e) { /* ignore */ }
    } catch (error) {
      toast.error('Σφάλμα αλλαγής PIN. Ελέγξτε το τρέχον PIN.');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/financial/entries`),
        axios.get(`${API}/financial/summary`)
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error('Σφάλμα φόρτωσης δεδομένων');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Calculate amount for hourly billing first, then validate
    let finalAmount = parseFloat(formData.amount);
    if (formData.entry_type === 'fee' && formData.fee_type === 'hourly') {
      const hours = parseFloat(formData.hours) || 0;
      const rate = parseFloat(formData.hourly_rate) || 0;
      finalAmount = hours * rate;
    }

    if (!finalAmount || finalAmount <= 0) {
      toast.error('Εισάγετε έγκυρο ποσό');
      return;
    }

    const payload = {
      entry_type: formData.entry_type,
      amount: finalAmount,
      description: formData.description || '',
      date: formData.date,
      client_id: formData.client_id || null,
      case_id: formData.case_id || null,
      fee_type: formData.fee_type || null,
      hours: formData.hours ? parseFloat(formData.hours) : null,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      notes: formData.notes || null,
      is_paid: formData.is_paid || false,
    };

    try {
      if (editingEntry) {
        await axios.put(`${API}/financial/entries/${editingEntry.id}`, payload);
        toast.success('Η εγγραφή ενημερώθηκε');
      } else {
        await axios.post(`${API}/financial/entries`, payload);
        toast.success('Η εγγραφή δημιουργήθηκε');
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Διαγραφή εγγραφής;')) return;
    try {
      await axios.delete(`${API}/financial/entries/${id}`);
      // Remove entry from state without reloading
      setEntries(prevEntries => prevEntries.filter(entry => entry.id !== id));
      // Update summary
      const deletedEntry = entries.find(e => e.id === id);
      if (deletedEntry) {
        setSummary(prev => ({
          ...prev,
          total_fees: deletedEntry.entry_type === 'fee' ? prev.total_fees - deletedEntry.amount : prev.total_fees,
          total_expenses: deletedEntry.entry_type === 'expense' ? prev.total_expenses - deletedEntry.amount : prev.total_expenses,
          paid_fees: (deletedEntry.entry_type === 'fee' && deletedEntry.is_paid) ? prev.paid_fees - deletedEntry.amount : prev.paid_fees,
          paid_expenses: (deletedEntry.entry_type === 'expense' && deletedEntry.is_paid) ? prev.paid_expenses - deletedEntry.amount : prev.paid_expenses
        }));
      }
      toast.success('Η εγγραφή διαγράφηκε');
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEntry(null);
    setFormData({
      entry_type: 'fee',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      client_id: '',
      case_id: '',
      fee_type: 'flat',
      hours: '',
      hourly_rate: '',
      notes: '',
      is_paid: false,
      add_to_case_entries: true
    });
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      entry_type: entry.entry_type,
      amount: entry.amount.toString(),
      description: entry.description,
      date: entry.date,
      client_id: entry.client_id || '',
      case_id: entry.case_id || '',
      fee_type: entry.fee_type || 'flat',
      hours: entry.hours?.toString() || '',
      hourly_rate: entry.hourly_rate?.toString() || '',
      notes: entry.notes || '',
      is_paid: entry.is_paid || false
    });
    setShowForm(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return '-';
    if (client.client_type === 'natural') {
      return removeAccents(`${client.surname} ${client.name}`.trim()).toUpperCase();
    }
    return removeAccents(client.company_name || '-').toUpperCase();
  };

  // Filtered entries
  const filteredEntries = useMemo(() => {
    let filtered = [...entries];
    // Filter only by client
    if (filterClient) {
      filtered = filtered.filter(e => e.client_id === filterClient);
    }
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [entries, filterClient]);

  // Οφειλές ανά πελάτη: χρεώσεις (αμοιβές + έξοδα) − πληρωμές > 0
  const clientDebts = useMemo(() => {
    const byClient = {};
    entries.forEach((e) => {
      if (!e.client_id) return;
      const b = byClient[e.client_id] || (byClient[e.client_id] = { charges: 0, payments: 0 });
      if (e.entry_type === 'payment') b.payments += e.amount || 0;
      else b.charges += e.amount || 0;
    });
    return Object.entries(byClient)
      .map(([clientId, b]) => ({ clientId, owed: b.charges - b.payments }))
      .filter((d) => d.owed > 0)
      .sort((a, b) => b.owed - a.owed);
  }, [entries]);

  // Render PIN entry screen
  if (!pinVerified) {
    if (checkingPin) {
      return (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="max-w-md p-0 gap-0 overflow-hidden [&>button]:hidden">
            <div className="flex items-center justify-center py-12 bg-slate-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    // Setting new PIN
    if (settingPin || !hasPin) {
      return (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="max-w-md p-0 gap-0 overflow-hidden [&>button]:hidden sm:rounded-2xl">
            <DialogHeader className="p-4 border-b border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-2">
                <DialogTitle className="flex items-center gap-2.5 text-base sm:text-lg font-semibold">
                  <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
                    <Lock className="w-4 h-4" />
                  </span>
                  <span>Ορισμός PIN</span>
                </DialogTitle>
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Κλείσιμο
                </button>
              </div>
            </DialogHeader>
            <div className="p-4 bg-slate-50">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4">
                <p className="text-sm text-slate-600">
                  Ορίστε ένα 4-ψήφιο PIN για την προστασία της οικονομικής διαχείρισης.
                </p>
                <div>
                  <Label className="text-sm text-slate-600">Νέο PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={newPinInput}
                    onChange={(e) => setNewPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center text-2xl tracking-widest mt-1.5 h-12 bg-white border-slate-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Επιβεβαίωση PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={confirmPinInput}
                    onChange={(e) => setConfirmPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center text-2xl tracking-widest mt-1.5 h-12 bg-white border-slate-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={onClose} className="flex-1 h-10 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-100 transition-all">
                    Ακύρωση
                  </button>
                  <button 
                    onClick={setPin} 
                    className="flex-1 h-10 bg-red-700 hover:bg-red-800 text-white font-medium rounded-lg shadow-md shadow-red-900/20 transition-all disabled:bg-slate-300"
                    disabled={newPinInput.length !== 4 || confirmPinInput.length !== 4}
                  >
                    Ορισμός PIN
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    // Enter existing PIN
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden [&>button]:hidden sm:rounded-2xl">
          <DialogHeader className="p-4 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="flex items-center gap-2.5 text-base sm:text-lg font-semibold">
                <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
                  <Lock className="w-4 h-4" />
                </span>
                <span>Οικονομική διαχείριση</span>
              </DialogTitle>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Κλείσιμο
              </button>
            </div>
          </DialogHeader>
          <div className="p-6 bg-slate-50">
            <div className="bg-white rounded-xl border border-slate-200 shadow-md p-6 space-y-5 max-w-sm mx-auto">
              <p className="text-base text-slate-600 text-center font-medium">
                Εισάγετε το 4-ψήφιο PIN
              </p>
              <div className="relative">
                <div className="flex justify-center gap-3 pointer-events-none">
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className={`w-14 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                        pinInput.length > index
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : pinInput.length === index
                            ? 'border-red-400 bg-white text-slate-400'
                            : 'border-slate-300 bg-white text-slate-400'
                      }`}
                    >
                      {pinInput.length > index ? '●' : ''}
                    </div>
                  ))}
                </div>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && verifyPin()}
                  autoFocus
                  autoComplete="off"
                  data-testid="financial-pin-input"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <div className="flex gap-3 pt-3">
                <button onClick={onClose} className="flex-1 h-11 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-100 transition-all">
                  Ακύρωση
                </button>
                <button 
                  onClick={verifyPin} 
                  className="flex-1 h-11 bg-red-700 hover:bg-red-800 text-white font-medium rounded-lg transition-all disabled:bg-slate-300"
                  disabled={pinInput.length !== 4}
                >
                  Είσοδος
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main Financial Management UI
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 [&>button]:hidden max-sm:!w-full max-sm:!max-w-full sm:rounded-2xl">
        <DialogHeader className="flex-shrink-0 p-3 sm:p-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2.5">
              <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
                <Wallet className="w-4 h-4" />
              </span>
              Οικονομική διαχείριση
            </DialogTitle>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                data-testid="financial-debts-btn"
                onClick={() => setShowDebts(!showDebts)}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  showDebts
                    ? 'bg-red-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Οφειλές
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`hidden sm:flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  showSettings 
                    ? 'bg-red-700 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span className="hidden sm:inline">Αλλαγή</span> PIN
              </button>
              <button
                onClick={onClose}
                className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Κλείσιμο
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-slate-50">
          {/* Settings Panel */}
          {showSettings && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-4">
              <h4 className="flex items-center gap-2.5 font-semibold text-slate-900 tracking-tight mb-3">Αλλαγή PIN</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm text-slate-600">Τρέχον PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center mt-1.5 h-10 bg-white border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Νέο PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={newPinInput}
                    onChange={(e) => setNewPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center mt-1.5 h-10 bg-white border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Επιβεβαίωση</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={confirmPinInput}
                    onChange={(e) => setConfirmPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center mt-1.5 h-10 bg-white border-slate-300"
                  />
                </div>
              </div>
              <button 
                onClick={changePin}
                className="mt-3 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-lg shadow-md shadow-red-900/20 transition-all duration-200 disabled:bg-slate-300"
                disabled={pinInput.length !== 4 || newPinInput.length !== 4 || confirmPinInput.length !== 4}
              >
                Αλλαγή PIN
              </button>
            </div>
          )}

          {/* Debts Panel — Οφειλές ανά πελάτη */}
          {showDebts && (
            <div data-testid="financial-debts-panel" className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-4">
              <h4 className="flex items-center gap-2.5 font-semibold text-slate-900 tracking-tight mb-3">
                Οφειλές πελατών ({clientDebts.length})
              </h4>
              {clientDebts.length === 0 ? (
                <p data-testid="financial-debts-empty" className="text-sm text-slate-400 text-center py-4">
                  Δεν υπάρχουν οφειλές — όλα εξοφλημένα
                </p>
              ) : (
                <>
                  <div className="divide-y divide-slate-100">
                    {clientDebts.map((d) => (
                      <button
                        key={d.clientId}
                        type="button"
                        data-testid={`debt-row-${d.clientId}`}
                        onClick={() => { setFilterClient(d.clientId); setShowDebts(false); }}
                        title="Προβολή καρτέλας πελάτη"
                        className="w-full flex items-center justify-between gap-3 py-2 px-1 hover:bg-slate-50 rounded transition-colors text-left"
                      >
                        <span className="text-sm font-semibold text-slate-800 truncate">{getClientName(d.clientId)}</span>
                        <span className="text-sm font-bold text-red-600 whitespace-nowrap">{formatCurrency(d.owed)}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-200">
                    <span className="text-sm font-semibold text-slate-600">Σύνολο οφειλών</span>
                    <span data-testid="financial-debts-total" className="text-base font-bold text-red-700 whitespace-nowrap">
                      {formatCurrency(clientDebts.reduce((s, d) => s + d.owed, 0))}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Summary Card - Only Balance */}
          {summary && (
            <div className="flex justify-end mb-4">
              <div className={`bg-white rounded-lg border shadow-sm px-3 py-2 ${summary.balance >= 0 ? 'border-green-200' : 'border-red-200'}`}>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span className="text-sm text-slate-500">Υπόλοιπο:</span>
                  <span className={`text-base font-bold ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(summary.balance)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Client Selection & Balance */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-6">
                {/* Client Dropdown */}
                <SearchableClientSelect
                  value={filterClient}
                  onChange={(val) => setFilterClient(val)}
                  clients={clients}
                  placeholder="Επιλέξτε πελάτη"
                  className="w-full sm:min-w-[220px]"
                  testId="financial-filter-client-select"
                  disableSearch
                />
                
                {/* Client Balance - only show when client is selected */}
                {filterClient && (
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-md border border-slate-200">
                    <span className="text-sm font-medium text-slate-600">Υπόλοιπο:</span>
                    <span className={`text-base font-bold whitespace-nowrap ${
                      (() => {
                        const clientEntries = entries.filter(e => e.client_id === filterClient);
                        const fees = clientEntries.filter(f => f.entry_type === 'fee').reduce((sum, f) => sum + (f.amount || 0), 0);
                        const expenses = clientEntries.filter(f => f.entry_type === 'expense').reduce((sum, f) => sum + (f.amount || 0), 0);
                        const payments = clientEntries.filter(f => f.entry_type === 'payment').reduce((sum, f) => sum + (f.amount || 0), 0);
                        const balance = fees + expenses - payments;
                        return balance >= 0 ? 'text-green-600' : 'text-red-600';
                      })()
                    }`}>
                      {(() => {
                        const clientEntries = entries.filter(e => e.client_id === filterClient);
                        const fees = clientEntries.filter(f => f.entry_type === 'fee').reduce((sum, f) => sum + (f.amount || 0), 0);
                        const expenses = clientEntries.filter(f => f.entry_type === 'expense').reduce((sum, f) => sum + (f.amount || 0), 0);
                        const payments = clientEntries.filter(f => f.entry_type === 'payment').reduce((sum, f) => sum + (f.amount || 0), 0);
                        const balance = fees + expenses - payments;
                        return formatCurrency(balance);
                      })()}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  // Pre-fill client_id if a client is selected
                  if (filterClient) {
                    setFormData(f => ({ ...f, client_id: filterClient }));
                  }
                  setShowForm(true);
                }}
                className="flex items-center gap-1 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-lg shadow-md shadow-red-900/20 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Νέα καταχώρηση
              </button>
            </div>
          </div>

        {/* Entry Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Entry Type - Top Level: Νέα χρέωση / Νέα πληρωμή */}
              <div>
                <Label>Είδος καταχώρησης</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(() => {
                    const isChargeActive = formData.entry_type === 'fee' || formData.entry_type === 'expense';
                    const isPaymentActive = formData.entry_type === 'payment';
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setFormData(f => ({ ...f, entry_type: f.entry_type === 'expense' ? 'expense' : 'fee' }))}
                          className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2.5 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            isChargeActive
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700'
                          }`}
                          data-testid="financial-new-charge-btn"
                        >
                          <span className="truncate">Νέα χρέωση</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(f => ({ ...f, entry_type: 'payment' }))}
                          className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2.5 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            isPaymentActive
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700'
                          }`}
                          data-testid="financial-new-payment-btn"
                        >
                          <span className="truncate">Νέα πληρωμή</span>
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Sub-toggle: Αμοιβή / Έξοδο — only when "Νέα χρέωση" is active */}
              {(formData.entry_type === 'fee' || formData.entry_type === 'expense') && (
                <div>
                  <Label>Τύπος χρέωσης</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {ENTRY_TYPES.filter(t => t.value !== 'payment').map(type => {
                      const TypeIcon = type.icon;
                      const isActive = formData.entry_type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData(f => ({ ...f, entry_type: type.value }))}
                          className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            isActive
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700'
                          }`}
                          data-testid={`financial-charge-type-${type.value}-btn`}
                        >
                          <span className="truncate">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Client + Case (moved to top) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Πελάτης (προαιρετικό)</Label>
                  <div className="mt-1">
                    <SearchableClientSelect
                      value={formData.client_id || ''}
                      onChange={(val) => setFormData(f => ({ ...f, client_id: val, case_id: '' }))}
                      clients={clients}
                      placeholder="Χωρίς πελάτη"
                      allowNone
                      noneLabel="Χωρίς πελάτη"
                      testId="financial-form-client-select"
                      disableSearch
                    />
                  </div>
                </div>

                <div>
                  <Label>Υπόθεση (προαιρετικό)</Label>
                  <Select value={formData.case_id || undefined} onValueChange={(val) => setFormData(f => ({ ...f, case_id: val === '_none_' ? '' : val }))} disabled={!formData.client_id}>
                    <SelectTrigger className="w-full mt-1 h-10 border border-slate-300 rounded-md text-sm text-slate-700">
                      <SelectValue placeholder="Χωρίς υπόθεση" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Χωρίς υπόθεση</SelectItem>
                      {cases.filter(c => c.client_id === formData.client_id).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {formatCaseName(c, caseEntries)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description (moved to top) */}
              <div>
                <Label>Περιγραφή</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="π.χ. Συμβουλευτική εργασία"
                  className="mt-1 text-sm placeholder:text-sm"
                />
              </div>

              {/* Fee Type (only for fees) - moved before amount/date */}
              {formData.entry_type === 'fee' && (
                <div>
                  <Label>Τρόπος αμοιβής</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {FEE_TYPES.map(ft => {
                      const FtIcon = ft.icon;
                      const isActive = formData.fee_type === ft.value;
                      return (
                        <button
                          key={ft.value}
                          type="button"
                          onClick={() => setFormData(f => ({ ...f, fee_type: ft.value }))}
                          className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            isActive
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700'
                          }`}
                        >
                          <span className="truncate">{ft.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Χρονοχρέωση: Ώρες × €/ώρα, then computed Ποσό, then Date */}
              {formData.entry_type === 'fee' && formData.fee_type === 'hourly' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Ώρες</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="π.χ. 2.5"
                        value={formData.hours}
                        onChange={(e) => setFormData(f => ({ ...f, hours: e.target.value }))}
                        className="mt-1 h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div>
                      <Label>Αμοιβή / ώρα (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="π.χ. 80"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData(f => ({ ...f, hourly_rate: e.target.value }))}
                        className="mt-1 h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Ποσό (€)</Label>
                    <Input
                      readOnly
                      value={(() => {
                        const h = parseFloat(formData.hours) || 0;
                        const r = parseFloat(formData.hourly_rate) || 0;
                        return (h * r).toFixed(2);
                      })()}
                      className="mt-1 h-9 text-sm bg-slate-50 text-slate-700"
                    />
                  </div>
                </>
              ) : (
                /* Πάγια αμοιβή / Έξοδο / Πληρωμή: Ποσό μόνο του */
                <div>
                  <Label>Ποσό (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData(f => ({ ...f, amount: e.target.value }))}
                    className="mt-1 h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              )}

              {/* Ημερομηνία (πάντα από κάτω σε ξεχωριστή σειρά) */}
              <div>
                <Label>Ημερομηνία</Label>
                <div className="space-y-1 mt-1">
                  <input
                    type="text"
                    value={formData.date ? new Date(formData.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/') : ''}
                    onChange={(e) => {
                      const parts = e.target.value.split('/');
                      if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10);
                        const year = parseInt(parts[2], 10);
                        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
                          const date = new Date(year, month - 1, day);
                          if (!isNaN(date.getTime())) {
                            setFormData(f => ({ ...f, date: date.toISOString().split('T')[0] }));
                          }
                        }
                      }
                    }}
                    placeholder="ηη/μμ/εεεε"
                    className="w-full h-8 px-3 py-1 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-0 focus:border-slate-400 bg-white"
                    autoComplete="off"
                  />
                  <div className="flex justify-center">
                    <DatePicker
                      selected={formData.date ? new Date(formData.date) : new Date()}
                      onChange={(date) => setFormData(f => ({ ...f, date: date ? date.toISOString().split('T')[0] : '' }))}
                      locale="el"
                      dateFormat="dd/MM/yyyy"
                      inline
                      fixedHeight
                      calendarClassName="small-calendar"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-lg">
                  Ακύρωση
                </Button>
                <Button type="submit" className="flex-1 bg-red-700 hover:bg-red-800 rounded-lg shadow-md shadow-red-900/20">
                  {editingEntry ? 'Ενημέρωση' : 'Αποθήκευση'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Entries List - Hidden when form is open */}
        {!showForm && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
            </div>
          ) : !filterClient ? (
            <div className="text-center py-12 text-slate-500">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <User className="w-6 h-6 text-slate-400" />
              </div>
              <p className="font-medium text-slate-900">Επιλέξτε πελάτη</p>
              <p className="text-sm text-slate-500">Επιλέξτε έναν πελάτη για να δείτε τα οικονομικά του στοιχεία</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Wallet className="w-6 h-6 text-slate-400" />
              </div>
              <p className="font-medium text-slate-900">Δεν υπάρχουν εγγραφές</p>
              <p className="text-sm text-slate-500">Δεν υπάρχουν οικονομικά στοιχεία για αυτόν τον πελάτη</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredEntries.map(entry => {
                return (
                  <div 
                    key={entry.id} 
                    className="p-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-slate-900 truncate">
                              {entry.description}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-xs text-slate-500">{new Date(entry.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                            <span className={`font-bold text-xs ${
                              entry.entry_type === 'fee' ? 'text-red-700' :
                              entry.entry_type === 'expense' ? 'text-amber-700' :
                              'text-slate-700'
                            }`}>
                              {entry.entry_type === 'payment' ? '-' : ''}{formatCurrency(entry.amount)}
                            </span>
                          </div>
                          {/* Client name row — desktop only (hidden on mobile to save space) */}
                          {entry.client_id && !filterClient && (
                            <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                              {getClientName(entry.client_id)}
                            </div>
                          )}
                          {entry.case_id && (
                            <p className="text-xs text-red-700 mt-1 truncate">
                              {(() => {
                                const caseItem = cases.find(c => c.id === entry.case_id);
                                return caseItem ? formatCaseName(caseItem, caseEntries) : 'Υπόθεση';
                              })()}
                            </p>
                          )}
                          {entry.notes && (
                            <p className="text-xs text-slate-400 mt-1 truncate">{entry.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(entry)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FinancialManagementModal;
